import { useTu } from "@src/appV2/i18n/useTu";
import { Box, Link } from "@mui/material";
import { SUPPORT_WEBFORM_URL } from "@src/appV2/ContactCBHInfo/constants";
import { Text } from "@src/appV2/redesign/components/Text";
import { type ReactNode } from "react";
import downloadQr from "./assets/download-qr.webp";
import phoneMockup from "./assets/phone-mockup.webp";
const clipboardWordmark = `${import.meta.env.BASE_URL}assets/clipboard-wordmark-brown.png`;

interface DesktopLoginLayoutProps {
  banner?: ReactNode;
  children: ReactNode;
}

export function DesktopLoginLayout({ banner, children }: DesktopLoginLayoutProps) {
  const { tu } = useTu("login");

  return (
    <Box
      sx={[
        {
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          display: "flex",
          // Top-align the panes instead of stretching them: when the form is
          // taller than the viewport and the container scrolls, the left pane
          // must grow to its full content height so its translucent background
          // covers everything (stretch caps it at one viewport, leaving a seam).
          alignItems: "flex-start",
          backgroundColor: "background.baseDark",
          overflow: "auto",
        },
        // Focus ring: the design system disables the ripple and has no focus-visible style.
        (theme) => ({
          "& :is(a, button):focus-visible": {
            outline: `2px solid ${theme.palette.base?.base ?? theme.palette.text.primary}`,
            outlineOffset: "2px",
          },
        }),
      ]}
    >
      {/* Left pane - login form */}
      <Box
        sx={{
          flex: "1 1 62.5%",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRight: "1px solid",
          borderColor: "border.base",
          backgroundColor: "rgba(255, 255, 255, 0.7)",
          paddingX: 5,
          paddingY: 8,
          minHeight: "100%",
        }}
      >
        <Box
          component="img"
          src={clipboardWordmark}
          alt="Clipboard"
          sx={{
            position: "absolute",
            top: 20,
            left: 30,
            height: 34,
            width: "auto",
          }}
        />

        {banner && <Box sx={{ width: "100%", maxWidth: 720, marginBottom: 4 }}>{banner}</Box>}

        {/* Spacers center the content when short and collapse to a minimum gap
            when tall, so it never slides under the footer. */}
        <Box sx={{ flex: 1, minHeight: 24 }} />

        <Box
          sx={{
            width: "100%",
            maxWidth: 400,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 5,
          }}
        >
          {children}
        </Box>

        <Box sx={{ flex: 1, minHeight: 24 }} />

        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            gap: 4,
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          <Text variant="body2" sx={{ color: "base.muted" }}>
            {`Clipboard Health \u00A9${new Date().getFullYear()}`}
          </Text>
          <Link
            href={SUPPORT_WEBFORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{ fontSize: "0.875rem", color: "primary.main" }}
          >
            {tu("Contact Us")}
          </Link>
        </Box>
      </Box>
      {/* Right pane - mobile app download promo */}
      <Box
        sx={{
          flex: "0 0 37.5%",
          minWidth: 420,
          position: "sticky",
          top: 0,
          alignSelf: "flex-start",
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          paddingX: 7,
          paddingY: 8,
        }}
      >
        <Box
          component="img"
          src={phoneMockup}
          alt=""
          sx={{
            height: "min(460px, 52vh)",
            width: "auto",
            marginTop: 8,
            filter: "drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.12))",
          }}
        />

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 4,
            width: "100%",
            maxWidth: 454,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
            <Text variant="h6" sx={{ color: "base.base" }}>
              {tu("Take Clipboard with you")}
            </Text>
            <Text variant="body2" sx={{ color: "base.muted" }}>
              {tu(
                "Download our mobile app to view your schedule, post shifts, and manage your workers on the go."
              )}
            </Text>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={downloadQr}
              alt="QR code to download the Clipboard mobile app"
              sx={{ width: 104, height: 104 }}
            />
            <Text variant="caption" sx={{ color: "base.muted" }}>
              {tu("Scan to download")}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
